import { createFileRoute } from '@tanstack/react-router'
import { AlternativesPage, alternativesHead, type AlternativesData } from '@/components/alternatives-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

const data: AlternativesData = {
  tool: "Datadog",
  slug: "datadog",
  intro: "Datadog is a full observability platform with usage-based billing for synthetics. If you only need uptime monitoring, or want a different observability stack, here are alternatives.",
  alternatives: [
    {
      name: "New Relic",
      description: "Full observability platform with a free tier of 100 GB ingest and 500 synthetic checks a month.",
      pricing: "Free (100 GB/mo), then per user plus $0.40/GB",
      pros: ["Free tier with 100 GB ingest", "APM, RUM, logs, and synthetics in one platform", "Multi-location alert conditions"],
      cons: ["Per-user pricing for full platform users", "Synthetic checks beyond 500 a month billed per check"],
      url: "https://newrelic.com",
    },
    {
      name: "Grafana Cloud",
      description: "Observability stack built on open-source tools (Prometheus, Loki, Tempo), with k6-based synthetic monitoring.",
      pricing: "Free tier, Pro from $19/mo plus usage",
      pros: ["Free tier with 100k API test executions a month", "Custom dashboards", "Built on open-source tools"],
      cons: ["Usage-based billing across several dimensions", "Scripted checks use k6 (JavaScript)"],
      url: "https://grafana.com/products/cloud/",
    },
    {
      name: "Uptrack",
      description: "If you only need Datadog for uptime monitoring, Uptrack offers 30-second checks with alert confirmation and flat plans from $3/mo.",
      pricing: "Free (5 monitors), Pro $3/mo, Team $20/mo, Business $79/mo",
      pros: ["30-second checks on every plan", "Alerts only after 2-5 consecutive failed checks", "Flat pricing with a 30-day money-back guarantee"],
      cons: ["Free plan is 5 monitors at 30 s from one location", MONITOR_LOCATIONS.alternativesCon, "No APM or logs"],
      url: "https://uptrack.app",
      isUptrack: true,
    },
    {
      name: "Better Stack",
      description: "Uptime monitoring combined with on-call, incident management, logs, and status pages in one platform.",
      pricing: "Free (10 monitors), paid from $34/mo per responder",
      pros: ["On-call scheduling and incident management", "Unlimited phone and SMS alerts on paid plans", "Multi-location confirmation (3 of 4 locations by default)"],
      cons: ["Priced per responder, plus monitor packs", "More product than you need if you only want uptime checks"],
      url: "https://betterstack.com",
    },
    {
      name: "Checkly",
      description: "Monitoring as code with Playwright browser checks, API checks, and CI/CD integration.",
      pricing: "Free (Hobby), Starter $24/mo",
      pros: ["Monitoring as code with Playwright", "CI/CD integration", "Configurable retry strategies"],
      cons: ["API and browser runs above plan limits are billed per run", "30-second checks start on Team ($64/mo)"],
      url: "https://www.checklyhq.com",
    },
  ],
  comparisonTable: [
    { tool: "New Relic", freePlan: "100 GB + 500 checks", checkInterval: "1min", alertConfirmation: "Configurable", startingPrice: "Per user + usage" },
    { tool: "Grafana Cloud", freePlan: "100k API executions", checkInterval: "10s and up", alertConfirmation: "Configurable", startingPrice: "$19/mo + usage" },
    { tool: "Uptrack", freePlan: "5 monitors at 30s", checkInterval: "30s (all plans)", alertConfirmation: "Yes (2-5 consecutive)", startingPrice: "Free; Pro $3/mo" },
    { tool: "Better Stack", freePlan: "10 monitors", checkInterval: "3min (free), 30s (paid)", alertConfirmation: "Yes (multi-location)", startingPrice: "$34/mo per responder" },
    { tool: "Checkly", freePlan: "Hobby (10 uptime monitors)", checkInterval: "2min (free), 30s (Team)", alertConfirmation: "Configurable retries", startingPrice: "$24/mo" },
  ],
}

export const Route = createFileRoute('/_site/alternatives/datadog')({
  component: () => <AlternativesPage data={data} />,
  head: () => alternativesHead(data),
})
